import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  PendingOrdersState,
  PendingOrder,
  OrderFilters,
  EscalationRequest,
  OrderStatus,
  OrderSummaryStats,
  EscalationReason,
  EscalationRecipient
} from '../../types/pendingOrders';
import { pendingOrdersService } from '../../services/pendingOrdersService';

// Async thunks
export const fetchOrders = createAsyncThunk(
  'pendingOrders/fetchOrders',
  async (filters: OrderFilters | undefined, { rejectWithValue }) => {
    try {
      const response = await pendingOrdersService.getOrders(filters);
      if (!response.success) {
        return rejectWithValue(response.error || 'Failed to fetch orders');
      }
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch orders');
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'pendingOrders/fetchOrderById',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const order = await pendingOrdersService.getOrderById(orderId);
      if (!order) {
        return rejectWithValue('Order not found');
      }
      return order;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch order details');
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'pendingOrders/updateOrderStatus',
  async ({ orderId, status }: { orderId: string; status: OrderStatus }, { rejectWithValue }) => {
    try {
      const response = await pendingOrdersService.updateOrderStatus(orderId, status);
      if (!response.success) {
        return rejectWithValue(response.error || 'Failed to update order status');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update order status');
    }
  }
);

export const escalateOrder = createAsyncThunk(
  'pendingOrders/escalateOrder',
  async (request: EscalationRequest, { rejectWithValue }) => {
    try {
      const response = await pendingOrdersService.escalateOrder(request);
      if (!response.success) {
        return rejectWithValue(response.error || 'Failed to escalate order');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to escalate order');
    }
  }
);

export const fetchEscalationReasons = createAsyncThunk(
  'pendingOrders/fetchEscalationReasons',
  async (_, { rejectWithValue }) => {
    try {
      const reasons = await pendingOrdersService.getEscalationReasons();
      return reasons;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch escalation reasons');
    }
  }
);

export const fetchEscalationRecipients = createAsyncThunk(
  'pendingOrders/fetchEscalationRecipients',
  async (_, { rejectWithValue }) => {
    try {
      const recipients = await pendingOrdersService.getEscalationRecipients();
      return recipients;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch escalation recipients');
    }
  }
);

// Initial state
const initialState: PendingOrdersState = {
  orders: [],
  filteredOrders: [],
  selectedOrder: null,
  filters: {},
  summaryStats: {
    totalPending: 0,
    lateOrders: 0,
    escalated: 0,
    averageWaitTime: 0,
    totalRevenue: 0,
    completionRate: 0
  },
  escalationReasons: [],
  escalationRecipients: [],
  loading: false,
  error: null,
  lastUpdated: new Date().toISOString()
};

// Helper function to apply filters
const applyFilters = (orders: PendingOrder[], filters: OrderFilters): PendingOrder[] => {
  let filtered = [...orders];

  if (filters.status?.length) {
    filtered = filtered.filter(order => filters.status!.includes(order.status));
  }

  if (filters.priority?.length) {
    filtered = filtered.filter(order => filters.priority!.includes(order.priority));
  }

  if (filters.restaurant?.length) {
    filtered = filtered.filter(order => filters.restaurant!.includes(order.restaurant.id));
  }

  if (filters.customer) {
    const searchTerm = filters.customer.toLowerCase();
    filtered = filtered.filter(order => 
      order.customer.name.toLowerCase().includes(searchTerm) ||
      order.customer.phone.includes(filters.customer!) ||
      order.orderNumber.toLowerCase().includes(searchTerm)
    );
  }

  if (filters.isEscalated !== undefined) {
    filtered = filtered.filter(order => order.isEscalated === filters.isEscalated);
  }

  if (filters.waitTimeRange) {
    filtered = filtered.filter(order => 
      order.waitingTime >= filters.waitTimeRange!.min &&
      order.waitingTime <= filters.waitTimeRange!.max
    );
  }

  if (filters.paymentStatus?.length) {
    filtered = filtered.filter(order => 
      filters.paymentStatus!.includes(order.paymentStatus)
    );
  }

  if (filters.orderType?.length) {
    filtered = filtered.filter(order => 
      filters.orderType!.includes(order.orderType)
    );
  }

  if (filters.dateRange) {
    const startDate = new Date(filters.dateRange.start);
    const endDate = new Date(filters.dateRange.end);
    filtered = filtered.filter(order => {
      const orderDate = new Date(order.createdAt);
      return orderDate >= startDate && orderDate <= endDate;
    });
  }

  return filtered;
};

// Slice
const pendingOrdersSlice = createSlice({
  name: 'pendingOrders',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<OrderFilters>) => {
      state.filters = action.payload;
      state.filteredOrders = applyFilters(state.orders, action.payload);
    },
    clearFilters: (state) => {
      state.filters = {};
      state.filteredOrders = [...state.orders];
    },
    selectOrder: (state, action: PayloadAction<PendingOrder | null>) => {
      state.selectedOrder = action.payload;
    },
    updateOrderInList: (state, action: PayloadAction<PendingOrder>) => {
      const index = state.orders.findIndex(order => order.id === action.payload.id);
      if (index !== -1) {
        state.orders[index] = action.payload;
        state.filteredOrders = applyFilters(state.orders, state.filters);
      }
    },
    addOrder: (state, action: PayloadAction<PendingOrder>) => {
      state.orders.unshift(action.payload);
      state.filteredOrders = applyFilters(state.orders, state.filters);
    },
    removeOrder: (state, action: PayloadAction<string>) => {
      state.orders = state.orders.filter(order => order.id !== action.payload);
      state.filteredOrders = applyFilters(state.orders, state.filters);
    },
    setRealTimeUpdates: (state, action: PayloadAction<PendingOrder[]>) => {
      state.orders = action.payload;
      state.filteredOrders = applyFilters(action.payload, state.filters);
      state.lastUpdated = new Date().toISOString();
    },
    clearError: (state) => {
      state.error = null;
    },
    updateSummaryStats: (state, action: PayloadAction<Partial<OrderSummaryStats>>) => {
      state.summaryStats = { ...state.summaryStats, ...action.payload };
    },
    sortOrders: (state, action: PayloadAction<{ field: keyof PendingOrder; direction: 'asc' | 'desc' }>) => {
      const { field, direction } = action.payload;
      
      const sortFunction = (a: PendingOrder, b: PendingOrder) => {
        let aValue = a[field];
        let bValue = b[field];

        // Handle nested objects
        if (field === 'customer') {
          aValue = a.customer.name;
          bValue = b.customer.name;
        } else if (field === 'restaurant') {
          aValue = a.restaurant.name;
          bValue = b.restaurant.name;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        if (aValue instanceof Date && bValue instanceof Date) {
          return direction === 'asc' 
            ? aValue.getTime() - bValue.getTime()
            : bValue.getTime() - aValue.getTime();
        }

        return 0;
      };

      state.filteredOrders.sort(sortFunction);
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.data;
        state.summaryStats = action.payload.stats;
        state.filteredOrders = applyFilters(action.payload.data, state.filters);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch order by ID
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update order status
      .addCase(updateOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload;
        
        // Update in orders array
        const orderIndex = state.orders.findIndex(order => order.id === updatedOrder.id);
        if (orderIndex !== -1) {
          state.orders[orderIndex] = updatedOrder;
        }
        
        // Update selected order if it's the same
        if (state.selectedOrder?.id === updatedOrder.id) {
          state.selectedOrder = updatedOrder;
        }
        
        // Reapply filters
        state.filteredOrders = applyFilters(state.orders, state.filters);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Escalate order
      .addCase(escalateOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(escalateOrder.fulfilled, (state, action) => {
        state.loading = false;
        const { orderId } = action.payload;
        
        // Update the order status to escalated
        const orderIndex = state.orders.findIndex(order => order.id === orderId);
        if (orderIndex !== -1) {
          state.orders[orderIndex].status = 'escalated';
          state.orders[orderIndex].isEscalated = true;
          state.orders[orderIndex].lastUpdated = new Date().toISOString();
        }
        
        // Reapply filters
        state.filteredOrders = applyFilters(state.orders, state.filters);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(escalateOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch escalation reasons
      .addCase(fetchEscalationReasons.fulfilled, (state, action) => {
        state.escalationReasons = action.payload;
      })
      .addCase(fetchEscalationReasons.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Fetch escalation recipients
      .addCase(fetchEscalationRecipients.fulfilled, (state, action) => {
        state.escalationRecipients = action.payload;
      })
      .addCase(fetchEscalationRecipients.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  selectOrder,
  updateOrderInList,
  addOrder,
  removeOrder,
  setRealTimeUpdates,
  clearError,
  updateSummaryStats,
  sortOrders
} = pendingOrdersSlice.actions;

export default pendingOrdersSlice.reducer;